/**
 * Notification API service for managing notifications.
 */

import apiClient from './api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export interface Notification {
  id: string;
  type: 'event_created' | 'event_approved' | 'event_rejected' | 'event_updated' | 'guest_response' | 'event_reminder' | 'system_alert';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  is_read: boolean;
  is_archived: boolean;
  event?: string;
  guest_event?: string;
  metadata: Record<string, any>;
  created_at: string;
  read_at?: string;
  expires_at?: string;
  event_details?: {
    id: string;
    title: string;
    event_date: string;
    event_time: string;
    location: string;
  };
  guest_event_details?: {
    id: string;
    guest_name: string;
    guest_phone: string;
    response_status: string;
  };
  event_title?: string;
  guest_name?: string;
}

export interface NotificationListResponse {
  success: boolean;
  data: Notification[];
  count: number;
  unread_count: number;
}

export interface NotificationStats {
  total_count: number;
  unread_count: number;
  read_count: number;
  type_counts: Record<string, number>;
  priority_counts: Record<string, number>;
}

class NotificationAPI {
  private baseURL = `${API_BASE_URL}/notifications/`;

  /**
   * Get notifications for the current user
   */
  async getNotifications(params: {
    limit?: number;
    unread_only?: boolean;
  } = {}): Promise<NotificationListResponse> {
    const response = await apiClient.get(this.baseURL, { params });
    return response.data;
  }

  /**
   * Get a specific notification by ID
   */
  async getNotification(id: string): Promise<{ success: boolean; data: Notification }> {
    const response = await apiClient.get(`${this.baseURL}${id}/`);
    return response.data;
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<{ success: boolean; unread_count: number }> {
    const response = await apiClient.get(`${this.baseURL}unread_count/`);
    return response.data;
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(id: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(`${this.baseURL}${id}/mark_read/`);
    return response.data;
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<{ success: boolean; message: string; updated_count: number }> {
    const response = await apiClient.post(`${this.baseURL}mark_all_read/`);
    return response.data;
  }

  // Archive function removed - no action buttons needed

  /**
   * Get notification statistics
   */
  async getStats(): Promise<{ success: boolean; data: NotificationStats }> {
    const response = await apiClient.get(`${this.baseURL}stats/`);
    return response.data;
  }

  /**
   * Get user's notification settings
   */
  async getSettings(): Promise<{ success: boolean; settings: NotificationSettings }> {
    const response = await apiClient.get(`${this.baseURL}get_notification_settings/`);
    return response.data;
  }

  /**
   * Update user's notification settings
   */
  async updateSettings(settings: Partial<NotificationSettings>): Promise<{ success: boolean; message: string; settings: NotificationSettings }> {
    const response = await apiClient.put(`${this.baseURL}update_notification_settings/`, settings);
    return response.data;
  }

  /**
   * Reset notification settings to defaults
   */
  async resetSettings(): Promise<{ success: boolean; message: string; settings: NotificationSettings }> {
    const response = await apiClient.post(`${this.baseURL}reset_notification_settings/`);
    return response.data;
  }

  // Delete function removed - only archive functionality is available
}

export interface NotificationSettings {
  event_created: boolean;
  event_approved: boolean;
  event_rejected: boolean;
  event_updated: boolean;
  guest_response: boolean;
  event_reminder: boolean;
  system_alert: boolean;
}

export const notificationAPI = new NotificationAPI();
export default notificationAPI;
